<template>
  <Card>
    <CardHeader>
      <CardTitle class="tw-flex tw-items-center tw-gap-2">
        <ShoppingCart class="tw-w-5 tw-h-5 tw-text-secondary-500" />
        <span class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
          {{ t('customer_management.titles.forms') }}
        </span>
      </CardTitle>
      <p class="tw-text-sm tw-text-secondary-500">
        {{ t('customer_management.form_config.description') }}
      </p>
    </CardHeader>
    <CardContent>
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
        <!-- Order Forms -->
        <div class="tw-space-y-6 tw-p-4 tw-border tw-border-border tw-rounded-lg">
          <div class="tw-flex tw-items-center tw-gap-2 tw-pb-2 tw-border-b tw-border-border">
            <ShoppingCart class="tw-w-5 tw-h-5 tw-text-secondary-500" />
            <h3 class="tw-text-lg tw-font-semibold tw-text-secondary-500 tw-m-0">
              {{ t('customer_management.titles.order_forms') }}
            </h3>
          </div>

          <!-- Order Required Fields -->
          <div class="tw-space-y-4 tw-p-3 tw-bg-muted/30 tw-rounded-md">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h4 class="tw-font-semibold tw-text-secondary-500 tw-m-0 tw-text-sm">
                {{ t('customer_management.form_config.required_fields') }}
              </h4>
              <span class="tw-text-sm tw-text-secondary-500">
                {{ validOrderRequired.length }}
                {{ t('customer_management.form_config.fields_selected') }}
              </span>
            </div>

            <!-- Current Order Required Fields -->
            <div class="tw-flex tw-flex-wrap tw-gap-2 tw-min-h-[2rem]">
              <Badge
                v-for="fieldId in validOrderRequired"
                :key="fieldId"
                variant="default"
                class="tw-gap-1 tw-bg-secondary-500 tw-text-secondary-500 tw-border-secondary-500 hover:tw-bg-secondary-700"
              >
                {{ getFieldNameById('order', fieldId) }}
                <button
                  type="button"
                  @click="removeField('orderRequired', fieldId)"
                  class="tw-ml-1 tw-rounded-sm tw-p-0.5 tw-text-white"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
              <span
                v-if="validOrderRequired.length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.no_fields_selected') }}
              </span>
            </div>

            <!-- Available Order Required Fields -->
            <div>
              <Label class="tw-text-sm tw-text-secondary-500 tw-mb-2 tw-block">
                {{ t('customer_management.form_config.available_fields') }}
              </Label>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Button
                  v-for="field in getAvailableFields('orderRequired')"
                  :key="field.id"
                  type="button"
                  variant="outline"
                  size="sm"
                  @click="addPredefinedField('orderRequired', field.id)"
                  class="tw-h-8 tw-px-3 tw-text-xs tw-text-secondary-500 tw-border-secondary-500/30 hover:tw-bg-secondary-500 hover:tw-text-white"
                >
                  <Plus class="tw-w-3 tw-h-3 tw-mr-1" />
                  {{ field.field_name }}
                </Button>
              </div>
              <p
                v-if="getAvailableFields('orderRequired').length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.all_selected') }}
              </p>
            </div>
          </div>

          <!-- Order Optional Fields -->
          <div class="tw-space-y-4 tw-p-3 tw-bg-muted/30 tw-rounded-md">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h4 class="tw-font-semibold tw-text-secondary-500 tw-m-0 tw-text-sm">
                {{ t('customer_management.form_config.optional_fields') }}
              </h4>
              <span class="tw-text-sm tw-text-secondary-500">
                {{ validOrderOptional.length }}
                {{ t('customer_management.form_config.fields_selected') }}
              </span>
            </div>

            <!-- Current Order Optional Fields -->
            <div class="tw-flex tw-flex-wrap tw-gap-2 tw-min-h-[2rem]">
              <Badge
                v-for="fieldId in validOrderOptional"
                :key="fieldId"
                variant="default"
                class="tw-gap-1 tw-bg-secondary-500 tw-text-secondary-500 tw-border-secondary-500 hover:tw-bg-secondary-700"
              >
                {{ getFieldNameById('order', fieldId) }}
                <button
                  type="button"
                  @click="removeField('orderOptional', fieldId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5 tw-text-white"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
              <span
                v-if="validOrderOptional.length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.no_fields_selected') }}
              </span>
            </div>

            <!-- Available Order Optional Fields -->
            <div>
              <Label class="tw-text-sm tw-text-secondary-500 tw-mb-2 tw-block">
                {{ t('customer_management.form_config.available_fields') }}
              </Label>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Button
                  v-for="field in getAvailableFields('orderOptional')"
                  :key="field.id"
                  type="button"
                  variant="outline"
                  size="sm"
                  @click="addPredefinedField('orderOptional', field.id)"
                  class="tw-h-8 tw-px-3 tw-text-xs tw-text-secondary-500 tw-border-secondary-500/30 hover:tw-bg-secondary-500 hover:tw-text-white"
                >
                  <Plus class="tw-w-3 tw-h-3 tw-mr-1" />
                  {{ field.field_name }}
                </Button>
              </div>
              <p
                v-if="getAvailableFields('orderOptional').length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.all_selected') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Payment Forms -->
        <div class="tw-space-y-6 tw-p-4 tw-border tw-border-border tw-rounded-lg">
          <div class="tw-flex tw-items-center tw-gap-2 tw-pb-2 tw-border-b tw-border-border">
            <svg
              class="tw-w-5 tw-h-5 tw-text-secondary-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              ></path>
            </svg>
            <h3 class="tw-text-lg tw-font-semibold tw-text-secondary-500">
              {{ t('customer_management.form_config.payment.title') }}
            </h3>
          </div>

          <!-- Payment Required Fields -->
          <div class="tw-space-y-4 tw-p-3 tw-bg-muted/30 tw-rounded-md">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h4 class="tw-font-semibold tw-text-secondary-500 tw-m-0 tw-text-sm">
                {{ t('customer_management.form_config.required_fields') }}
              </h4>
              <span class="tw-text-sm tw-text-secondary-500">
                {{ validPaymentRequired.length }}
                {{ t('customer_management.form_config.fields_selected') }}
              </span>
            </div>

            <!-- Current Payment Required Fields -->
            <div class="tw-flex tw-flex-wrap tw-gap-2 tw-min-h-[2rem]">
              <Badge
                v-for="fieldId in validPaymentRequired"
                :key="fieldId"
                variant="default"
                class="tw-gap-1 tw-bg-secondary-500 tw-text-secondary-500 tw-border-secondary-500 hover:tw-bg-secondary-700"
              >
                {{ getFieldNameById('payment', fieldId) }}
                <button
                  type="button"
                  @click="removeField('paymentRequired', fieldId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5 tw-text-white"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
              <span
                v-if="validPaymentRequired.length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.no_fields_selected') }}
              </span>
            </div>

            <!-- Available Payment Required Fields -->
            <div>
              <Label class="tw-text-sm tw-text-secondary-500 tw-mb-2 tw-block">
                {{ t('customer_management.form_config.available_fields') }}
              </Label>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Button
                  v-for="field in getAvailableFields('paymentRequired')"
                  :key="field.id"
                  type="button"
                  variant="outline"
                  size="sm"
                  @click="addPredefinedField('paymentRequired', field.id)"
                  class="tw-h-8 tw-px-3 tw-text-xs tw-text-secondary-500 tw-border-secondary-500/30 hover:tw-bg-secondary-500 hover:tw-text-white"
                >
                  <Plus class="tw-w-3 tw-h-3 tw-mr-1" />
                  {{ field.field_name }}
                </Button>
              </div>
              <p
                v-if="getAvailableFields('paymentRequired').length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.all_selected') }}
              </p>
            </div>
          </div>

          <!-- Payment Optional Fields -->
          <div class="tw-space-y-4 tw-p-3 tw-bg-muted/30 tw-rounded-md">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h4 class="tw-font-semibold tw-text-secondary-500 tw-m-0 tw-text-sm">
                {{ t('customer_management.form_config.optional_fields') }}
              </h4>
              <span class="tw-text-sm tw-text-secondary-500">
                {{ validPaymentOptional.length }}
                {{ t('customer_management.form_config.fields_selected') }}
              </span>
            </div>

            <!-- Current Payment Optional Fields -->
            <div class="tw-flex tw-flex-wrap tw-gap-2 tw-min-h-[2rem]">
              <Badge
                v-for="fieldId in validPaymentOptional"
                :key="fieldId"
                variant="default"
                class="tw-gap-1 tw-bg-secondary-500 tw-text-secondary-500 tw-border-secondary-500 hover:tw-bg-secondary-700"
              >
                {{ getFieldNameById('payment', fieldId) }}
                <button
                  type="button"
                  @click="removeField('paymentOptional', fieldId)"
                  class="tw-ml-1 hover:tw-bg-destructive/20 tw-rounded-sm tw-p-0.5 tw-text-white"
                >
                  <X class="tw-w-3 tw-h-3" />
                </button>
              </Badge>
              <span
                v-if="validPaymentOptional.length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.no_fields_selected') }}
              </span>
            </div>

            <!-- Available Payment Optional Fields -->
            <div>
              <Label class="tw-text-sm tw-text-secondary-500 tw-mb-2 tw-block">
                {{ t('customer_management.form_config.available_fields') }}
              </Label>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Button
                  v-for="field in getAvailableFields('paymentOptional')"
                  :key="field.id"
                  type="button"
                  variant="outline"
                  size="sm"
                  @click="addPredefinedField('paymentOptional', field.id)"
                  class="tw-h-8 tw-px-3 tw-text-xs tw-text-secondary-500 tw-border-secondary-500/30 hover:tw-bg-secondary-500 hover:tw-text-white"
                >
                  <Plus class="tw-w-3 tw-h-3 tw-mr-1" />
                  {{ field.field_name }}
                </Button>
              </div>
              <p
                v-if="getAvailableFields('paymentOptional').length === 0"
                class="tw-text-sm tw-text-secondary-500 tw-italic"
              >
                {{ t('customer_management.form_config.all_selected') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
    <StepFooter
      v-if="!isEdit"
      :current-step="3"
      :total-steps="4"
      :loading="false"
      :on-validate="validateForm"
      @previous="handlePrevious"
      @next="handleNext"
      @finish="handleFinish"
      class="tw-px-6 tw-pb-6"
    />

    <div v-if="isEdit && onSave" class="tw-flex tw-px-6 tw-pb-6 tw-justify-end">
      <SectionSaveButton
        :on-save="onSave"
        :is-edit="isEdit"
        :section-name="t('customer_management.form_config.title')"
      />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useCustomerStore } from '@/stores/customer.store';
import { Plus, ShoppingCart, X } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionSaveButton from './SectionSaveButton.vue';
import StepFooter from './StepFooter.vue';
const { t } = useI18n();

// Types
interface FormsSectionData {
  forms: {
    orderRequired: number[];
    orderOptional: number[];
    paymentRequired: number[];
    paymentOptional: number[];
  };
}

type SaveResult = { success: boolean; message: string };

interface FormsSectionProps {
  data: FormsSectionData;
  isEdit: boolean;
  onSave?: () => SaveResult | Promise<SaveResult>;
}

// Props
const props = defineProps<FormsSectionProps>();

// Emits
const emit = defineEmits<{
  change: [updates: FormsSectionData];
  previous: [];
  next: [];
  finish: [];
}>();

// Field type from API
type FieldOption = { id: number; field_name: string };

const customerStore = useCustomerStore();
const orderAvailable = ref<FieldOption[]>([]);
const paymentAvailable = ref<FieldOption[]>([]);

const fieldMapById = computed(() => {
  const map: Record<string, Record<number, string>> = {
    order: {},
    payment: {},
  };
  orderAvailable.value.forEach(f => (map.order[f.id] = f.field_name));
  paymentAvailable.value.forEach(f => (map.payment[f.id] = f.field_name));
  return map;
});

// Filter out deleted fields (fields that no longer exist in available fields)
const validOrderRequired = computed(() =>
  props.data.forms.orderRequired.filter(id => fieldMapById.value.order[id])
);
const validOrderOptional = computed(() =>
  props.data.forms.orderOptional.filter(id => fieldMapById.value.order[id])
);
const validPaymentRequired = computed(() =>
  props.data.forms.paymentRequired.filter(id => fieldMapById.value.payment[id])
);
const validPaymentOptional = computed(() =>
  props.data.forms.paymentOptional.filter(id => fieldMapById.value.payment[id])
);

// Computed
const getAvailableFields = computed(() => {
  return (category: keyof typeof props.data.forms) => {
    const formType = category.startsWith('order') ? 'order' : 'payment';

    // Get all selected field IDs for this form type (both required and optional)
    const allSelectedIds = new Set([
      ...(formType === 'order'
        ? [...props.data.forms.orderRequired, ...props.data.forms.orderOptional]
        : [...props.data.forms.paymentRequired, ...props.data.forms.paymentOptional]),
    ]);

    return (formType === 'order' ? orderAvailable.value : paymentAvailable.value).filter(
      f => !allSelectedIds.has(f.id)
    );
  };
});

// Methods
const removeField = (category: keyof typeof props.data.forms, fieldId: number) => {
  const updatedForms = { ...props.data.forms };
  updatedForms[category] = updatedForms[category].filter(f => f !== fieldId);
  emit('change', { forms: updatedForms });
};

const addPredefinedField = (category: keyof typeof props.data.forms, fieldId: number) => {
  const updatedForms = { ...props.data.forms };
  if (!updatedForms[category].includes(fieldId)) {
    updatedForms[category] = [...updatedForms[category], fieldId];
    emit('change', { forms: updatedForms });
  }
};

// Validation function for StepFooter
const validateForm = (): { isValid: boolean; errors: string[] } => {
  // Form configuration is generally optional in most cases
  // We'll allow empty configurations and let business logic handle requirements
  return {
    isValid: true, // Always valid for now
    errors: [],
  };
};

const handlePrevious = () => {
  emit('previous');
};

const handleNext = () => {
  emit('next');
};

const handleFinish = () => {
  emit('finish');
};

const getFieldNameById = (type: 'order' | 'payment', id: number): string => {
  const name = fieldMapById.value[type][id];
  return name ?? String(id);
};

onMounted(async () => {
  try {
    orderAvailable.value = await customerStore.getOrderFields();
  } catch (e) {
    orderAvailable.value = [];
  }
  try {
    paymentAvailable.value = await customerStore.getPaymentsFields();
  } catch (e) {
    paymentAvailable.value = [];
  }
});
</script>
